import { useMemo } from 'react';

import { useSession } from '../../contexts/session-context';
import { UserType, type User } from '../../types/api';

/**
 * Suspended-account state for the signed-in user (M1.1a §7, wired to a real signal by ROBO-478).
 *
 * THE SIGNAL. `AuthenticatedResponse.suspended` (`AuthResponse.suspended`), carried onto the session
 * user by `buildUserFromAuthResponse` and refreshed with every login, authorize and token refresh.
 * The API sets it `true` only for the Owner of a suspended business (parent rollup included). Every
 * other user of a suspended business is refused a session outright — a 403 `BRAND_SUSPENDED`,
 * surfaced by the login screen through `useSession().refusal` — so a non-Owner never reaches this
 * hook with the flag set. Admins, and Admin-super-user sessions, always get `false`.
 *
 * WHAT LEGACY DOES WITH IT, which is the whole contract:
 *
 * 1. Suspension routing happens at login only (`Login/login.asp:75-83`): a suspended Owner lands on
 *    `PAYMENTS_SCREEN`, a suspended non-Owner on `/inactiveaccount.asp`, an Admin is unaffected.
 * 2. There is NO per-page suspension gate. `ForceSuspendedBusinessToPaymentsPage()`
 *    (`inc/functions_routing.asp:18`) is never called — the only non-definition reference is a
 *    commented-out line at `inc/const.asp:72`. So an Owner who navigates away from the payments
 *    screen is not sent back, and the dashboard renders normally for them.
 *
 * So `landingPage` is a SIGN-IN decision (`pages/login/post-login-destination.ts`), not something a
 * page should enforce on every render. Adding a gate here would be new behaviour and needs a product
 * decision, not a parity ticket. `suspended` is a display and routing signal; the API is the
 * enforcement layer.
 */
export interface SuspendedAccountState {
    /** Whether the caller's brand (or its parent brand) is suspended. */
    isSuspended: boolean;
    /**
     * Where legacy would send this user at login: Owners to the payments screen, everyone else to
     * the inactive-account page, Admins nowhere. `undefined` when not suspended.
     */
    landingPage: 'payments' | 'inactive' | undefined;
}

const NOT_SUSPENDED: SuspendedAccountState = {
    isSuspended: false,
    landingPage: undefined
};

/**
 * The pure rule behind {@link useSuspendedAccount}, exported for the sign-in landing decision and
 * for tests. Mirrors `Login/login.asp:75-83`.
 *
 * A flag on a non-Owner should not happen (the API refuses those users instead), but if it ever
 * does, the legacy branch for them is the inactive page, not payments. An Admin is never treated as
 * suspended, whatever the flag says — legacy exempts userType 1 explicitly.
 */
export function resolveSuspendedAccount(
    user: Pick<User, 'userType' | 'suspended'> | undefined
): SuspendedAccountState {
    if (user?.suspended !== true || user.userType === UserType.Admin) {
        return NOT_SUSPENDED;
    }
    return {
        isSuspended: true,
        landingPage: user.userType === UserType.Owner ? 'payments' : 'inactive'
    };
}

export function useSuspendedAccount(): SuspendedAccountState {
    const { user } = useSession();
    return useMemo(() => resolveSuspendedAccount(user), [user]);
}
